/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2018, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../lib/@waca/core-client/js/core-client/ui/core/View',
	'underscore',
	'../widgets/livewidget/nls/StringResources',
	'react',
	'react-dom',
	'@ba/ba-ui-carbon-toolkit'
], function (View, _, stringResources, React, ReactDOM, Toolkit) {
	/* eslint react/prop-types: 0 */ // TODO: Remove once we have prop-types brought in from glass.

	const { ToggleSwitch, FlexLayout, FlexItem, Label, NumberInput } = Toolkit;

	const minNumberOfBins = 5;
	const maxNumberOfBins = 100;

	class TitleDiv extends React.Component {
		constructor(props) {
			super(props);
			this.numberInputRef = React.createRef();
		}

		componentDidMount() {
			this.props.passNumberInputRef(this.numberInputRef);
		}

		render() {
			const titleLabel = stringResources.get('autoBinActionText');
			const binCountLabel = stringResources.get('propAutoBinCount');
			const titleId = 'ag-toggle-switch';
			const binCountId = 'ag-number-input';

			const toggleSection = <FlexLayout
				direction='row' justifyContent='space-between'
			>
				<FlexItem className='flexItem' gutter={[0.5, 0, 0.5, 0]} alignSelf='flex-end'>
					<Label
						id={titleId}
						label={titleLabel}
					/>
				</FlexItem>
				<FlexItem className='flexItem' gutter={[0.5, 0, 0.5, 0]} alignSelf='flex-end'>
					<ToggleSwitch
						className={this.props.toggleChecked ? 'ba-common-toggleSwitch is-checked': 'ba-common-toggleSwitch'} // classname is used in functional test
						checked={this.props.toggleChecked}
						onChange={this.props.onToggleChange}
						small={true}
						aria-labelledby={titleId}
					/>
				</FlexItem>
			</FlexLayout>;

			const numberOfBinsSection = <FlexLayout
				className='numberOfBinsSection'
				fullHeight
				direction='column'
				justifyContent='space-between'
			>
				<Label
					id={ binCountId }
					label={ binCountLabel }
					type='caption'
					disabled={ !this.props.toggleChecked }
				/>
				<NumberInput
					className={this.props.toggleChecked ? 'numberOfBinsInput': 'numberOfBinsInput is-disabled'} // classname is used in functional test
					value={ this.props.numberOfBins }
					max={ maxNumberOfBins }
					min={ minNumberOfBins }
					scale={0}
					// In order to leverage the toolkit nls strings we need to use onValueAccept prop instead of the onChange.
					// there are a number of benefits of using onValueAccept instead of onChange.
					// more details here: https://jsw.ibm.com/browse/CADBC-2054
					onValueAccept={n => this.props.numberOfBinsChanged(n)}
					disabled={ !this.props.toggleChecked }
					ref={ this.numberInputRef }
					light
				/>
			</FlexLayout>;

			return (
				<div className="content">
					{toggleSection}
					{numberOfBinsSection}
				</div>
			);
		}

	}

	/*
	* Class to render the react flyout components
	*/
	class ReactComponents extends React.Component {
		/*
		* The props hold a state that should be used as this classes state.
		* When the state changes the component gets re-rendered. So when the
		* toggle state or checkbox state changes we update the state to re-render
		* the component while also calling a callback to tell the parent flyout
		* of the event.
		*/
		constructor(props) {
			super(props);
			this.state = _.clone(props.state);
			this.debouncedAutobinTrigger = this.props.getDebouncedAutobinTrigger();
		}
		/*
		* Toggle switch state changed.
		*/
		toggleChanged() {
			const newState = _.extend({}, this.state, { toggleChecked: !this.state.toggleChecked });
			this.setState(newState);
			this.props.onToggleStateChanged(newState);
		}

		numberOfBinsChanged(newNumber) {
			if (this.state.toggleChecked == false) {
				return;
			}
			var parsed = parseInt(newNumber, 10);
			if (isNaN(parsed)) { return; }

			if (this.state.numberOfBins !=  parsed) {
				this.setState({ numberOfBins: parsed });
				if (parsed <= maxNumberOfBins && parsed >= minNumberOfBins) {
					this.debouncedAutobinTrigger(parsed);
				}
			}
		}

		render() {
			return (
				<TitleDiv onToggleChange={() => this.toggleChanged()}
					passNumberInputRef={(numberInputRef) => this.props.passNumberInputRef(numberInputRef)}
					numberInputRef={this.props.numberInputRef}
					toggleChecked={this.state.toggleChecked}
					numberOfBins={this.state.numberOfBins}
					numberOfBinsChanged={(newNumber) => this.numberOfBinsChanged(newNumber)} />
			);
		}
	}

	/**
	 * @classdesc  Creates an autobin dialog
	 *
	 * @public
	 *
	 * @param {object} options.slot - The data slot
	 * @param {object} options.widget - The widget
	 */

	class AutobinView extends View {
		constructor(...args) {
			super(...args);
			const options = args[0] || {};
			this._iWidth = '240';
			this.height = options.state.height;
			this.width = options.state.width;
			this.defaultNumberOfBins = options.state.defaultNumberOfBins;
			this.autoBinEventHandler = options.actions.apply;
			this.numberInputRef = React.createRef();
			let binning = options.state.binningStateCb();
			if (binning && binning.bins) {
				this.binningState = {
					numberOfBins: binning.bins
				};
			}
		}

		remove() {
			if (this.$el) {
				ReactDOM.unmountComponentAtNode(this.$el[0]);
			}
			this.viewState = null;
			return super.remove();
		}

		getElement(state) {
			return (
				<ReactComponents
					state={state}
					numberInputRef={this.numberInputRef}
					onToggleStateChanged={(newState) => this.onToggleChange(newState)}
					passNumberInputRef={(ref) => this._getNumberInputRef(ref)}
					getDebouncedAutobinTrigger={() => this.getDebouncedAutobinTrigger()}
				/>
			);
		}

		render() {
			this.$el
				.addClass('popoverDialogContainer autoBinAction')
				.css('min-width', this._iWidth + 'px');

			var numberOfBins;
			if (this.binningState && this.binningState.numberOfBins) {
				numberOfBins = this.binningState.numberOfBins;
			} else {
				numberOfBins = this.defaultNumberOfBins;
			}

			const state = {
				'toggleChecked': this.binningState ? true : false,
				'numberOfBins': numberOfBins
			};

			this.reactComponents = ReactDOM.render(
				this.getElement(state),
				this.$el[0]
			);

			return this;
		}
		/**
		 * @override overrides the setFocus from authoring toolbar class that is invoked from Flyout class
		 * focus will be set in the input if the auto group is in enabled state (see _setFocusOnNumberInput)
		 */
		setFocus() {
			this._setFocusOnNumberInput();
		}

		onToggleChange(reactState) {
			if (this.binningState) {
				this.binningState = null;
			} else {
				this.binningState = {
					numberOfBins: reactState.numberOfBins
				};
			}
			if (this.binningState) {
				this.autoBinEventHandler(this.binningState);
			} else {
				this.autoBinEventHandler(null);
			}
		}

		getDebouncedAutobinTrigger() {
			return _.debounce((numberOfBins) => this.numberOfBinsChanged(numberOfBins), 500);
		}

		numberOfBinsChanged(numberOfBins) {
			if (this.binningState) {
				this.binningState.numberOfBins = numberOfBins;
				this.autoBinEventHandler(this.binningState);
			}
		}

		_getNumberInputRef(ref) {
			this._numberInputRef = ref;
		}
		/**
		 * calls focus from toolkit NumberInput component if the auto-group is enabled
		 */
		_setFocusOnNumberInput() {
			if (this.binningState) {
				this._numberInputRef && this._numberInputRef.current.focus();
			}
		}
	}

	return AutobinView;
});
